/**
 * InstallRoute (Plan 06-03, Task 1).
 *
 * The `/installer` walkthrough screen for iOS Add-to-Home-Screen
 * installation. Reachable WITHOUT a session (06-UI-SPEC D-05) — install
 * happens before any auth, so the route must be public.
 *
 * Layout (06-UI-SPEC §"Layout Shapes" 3):
 *   Mobile (< lg): single column, video stacked above step list, full-
 *                  width primary CTA at the bottom.
 *   Tablet (>= lg): two-column grid; left = logo + h1 + subhead + steps
 *                   + CTA; right = video preview (max-w-90).
 *
 * On-mount focus: H1 (tabIndex=-1 + .focus()) so screen-readers and
 * keyboard users land on the page title — UI-SPEC §"Accessibility
 * Contract" → §"Focus management".
 *
 * The "Ferdig" CTA navigates back to the SPA root (`/`). It uses
 * `window.location.href = '/'` rather than React Router's navigate()
 * because the install walkthrough is the user's first interaction
 * with the installed PWA — a full-page reload guarantees the SW
 * lifecycle picks up cleanly and the dashboard route mounts fresh.
 *
 * i18n keys (06-01 Task 4):
 *   install.page_aria · install.h1 · install.subhead
 *   install.video_aria · install.video_fallback
 *   install.steps_aria · install.step{1..4}.* · install.cta.done · install.subnote
 */
import { useEffect, useRef, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, StellMark } from '../../components/primitives';
import { InstallDemoVideo } from './InstallDemoVideo';
import { InstallStepList } from './InstallStepList';

export function InstallRoute(): JSX.Element {
  const { t } = useTranslation();
  const h1Ref = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    h1Ref.current?.focus();
  }, []);

  const onDone = () => {
    window.location.href = '/';
  };

  return (
    <main
      role="main"
      aria-label={t('install.page_aria')}
      className="min-h-screen px-6 pt-12 pb-safe lg:grid lg:grid-cols-2 lg:gap-8 lg:p-8 lg:items-center"
    >
      {/* Left column (mobile: full-width; tablet: left half) */}
      <div className="flex flex-col">
        <div className="flex justify-center lg:justify-start">
          <StellMark size={48} />
        </div>
        <h1
          ref={h1Ref}
          tabIndex={-1}
          className="mt-6 mb-2 text-display-s font-display font-semibold text-center lg:text-left text-ink outline-none"
        >
          {t('install.h1')}
        </h1>
        <p className="text-sm text-ink-2 text-center lg:text-left">
          {t('install.subhead')}
        </p>

        {/* Mobile video: appears here, between subhead and step list.
            Tablet video is rendered in the right column below. */}
        <div className="lg:hidden">
          <InstallDemoVideo className="mt-6 aspect-[9/19.5]" />
        </div>

        <InstallStepList />

        <Button
          variant="primary"
          aria-label={t('install.cta.done')}
          className="mt-8 w-full h-12 text-base"
          onClick={onDone}
        >
          {t('install.cta.done')}
        </Button>
        <p className="mt-3 text-xs text-ink-2 text-center lg:text-left">
          {t('install.subnote')}
        </p>
      </div>

      {/* Right column (tablet only): the demo video preview. */}
      <div className="hidden lg:flex lg:items-center lg:justify-center">
        <InstallDemoVideo className="max-w-90 rounded-xl shadow-lg" />
      </div>
    </main>
  );
}
